// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, { CartLineContainerStyles } from "@turnerBlueprint/App/Components/Cart/CartLineContainer";
import Notify from "@turnerBlueprint/App/Components/Notify";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import ProductPartNumbers from "@turnerBlueprint/Overrides/Components/ProductPartNumbers";
import { parseBoolean } from "@turnerBlueprint/XDL/Utilities";
import React, { Dispatch, FC } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    selectedLines: (state as CustomAppState).xCart.selectedLines,
});

// XNG-Changes
const mapDispatchToProps = {
    xCartSelectLine: (key: string, value: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CartPage/SelectLine",
            parameter: { key, value },
        } as any);
    },
};

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    showAddToList?: boolean;
}

// XNG-Changes
type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    productBinNumberGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    cartLineContainer?: CartLineContainerStyles;
    productInfoContainer?: GridContainerProps;
    productPartNumbers?: ProductPartNumbersStyles;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceHeadingAndText?: SmallHeadingAndTextStyles;
    selectLineCheckboxWrapper?: InjectableCss;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    productBrandAndDescriptionGridItem: {
        width: [12, 12, 12, 5, 4],
        css: css`
            flex-direction: column;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: { width: [12, 12, 12, 4, 5] },
    productPriceAndAvailabilityGridItem: {
        width: 7,
        css: css`
            flex-direction: column;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    quantityGridItem: { width: 5 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productBinNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
};

const CartLineCardCondensed: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    removeCartLine,
    updateCartLine,
    extendedStyles,
    showRemoveAction,
    showVmiBinDetails,
    // XNG-Changes
    showAddToList,
    enableVat,
    vatPriceDisplay,
    selectedLines,
    xCartSelectLine,
}) => {
    const currentMode = getCookie(NavigationModeCookieName);
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);
    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));

    // XNG-Changes: Structure and use CartLineContainer
    return (
        <CartLineContainer
            extendedStyles={styles.cartLineContainer}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            isCondensed={true}
            includeLeftActions={true}
        >
            {/* 
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem> 
            */}
            {/* <XNG-Changes> */}
            <CartLineContainer.LeftActions>
                {showAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Checkbox
                        checked={selectedLines[cartLine.productId!]}
                        onChange={() => xCartSelectLine(cartLine.productId!, !selectedLines[cartLine.productId!])}
                        data-test-selector="cartline_Checkbox"
                    >
                        <VisuallyHidden>{translate("Add Cart line to list")}</VisuallyHidden>
                    </Checkbox>
                )}
            </CartLineContainer.LeftActions>
            {/* </XNG-Changes> */}
            <CartLineContainer.Description {...styles.cartLineInfoGridItem}>
                {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                {/* <Typography {...styles.productErpNumberText}>{cartLine.erpNumber}</Typography> */}

                <StyledWrapper {...styles.productInfoContainer}>
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={true}
                        showManufacturerItem={false}
                    />
                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <ProductBinNumbers
                            locationId={cart.vmiLocationId}
                            binId={cartLine.vmiBinId}
                            isCondensed={false}
                            extendedStyles={styles.productBinNumbers}
                        />
                    )}
                    {showInventoryAvailability && !cartLine.quoteRequired && (
                        <ProductAvailability
                            productId={cartLine.productId!}
                            availability={cartLine.availability!}
                            unitOfMeasure={cartLine.unitOfMeasure}
                            trackInventory={cartLine.trackInventory}
                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                            extendedStyles={styles.productAvailability}
                        />
                    )}
                </StyledWrapper>

                {errorMessages.length > 0 &&
                    errorMessages.map((message, index) => (
                        <Notify
                            sizeVariant="small"
                            type="danger"
                            showClose={false}
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            data-test-selector="cartline_errorMessage"
                        >
                            {message}
                        </Notify>
                    ))}
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={false}
                        showInvalidPriceMessage={true}
                        extendedStyles={styles.productPrice}
                        // XNG-Changes
                        label={translate("Price/Unit")}
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    editable={true}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    // XNG-Changes
                    {...{
                        unEligibleQty:
                            BLUEPRINT_NAME === "turnerScienceBlueprint" &&
                            !parseBoolean(cartLine.properties.isEligibleQty),
                    }}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        {/* XNG-Changes: use  SmallHeadingAndText */}
                        <SmallHeadingAndText
                            {...styles.extendedUnitNetPriceHeadingAndText}
                            data-test-selector="cartline_extendedUnitNetPrice"
                            heading={translate("Total")}
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                            }
                        />

                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.subtotalWithoutVatText}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Remove>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </CartLineContainer.Remove>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardCondensed));
